import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Ticket, Plus, Edit, Trash2, Save, Percent, DollarSign, Calendar, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromotionsManagement = ({ token, language }) => {
  const [promotions, setPromotions] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', description: '', discount_type: 'percentage',
    discount_value: '', min_order_value: '', max_discount: '',
    max_uses: '', max_uses_per_user: '1', valid_from: '', valid_until: '',
    is_active: true, applicable_to: 'all', restaurant_ids: []
  });

  const fetchPromotions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/promotions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPromotions(await res.json());
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => {
    fetchPromotions();
    fetch(`${API_URL}/api/restaurants`).then(r => r.ok ? r.json() : []).then(setRestaurants).catch(() => {});
  }, [fetchPromotions]);

  const openNew = () => {
    setEditData(null);
    setForm({ code: '', name: '', description: '', discount_type: 'percentage', discount_value: '', min_order_value: '', max_discount: '', max_uses: '', max_uses_per_user: '1', valid_from: '', valid_until: '', is_active: true, applicable_to: 'all', restaurant_ids: [] });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditData(p);
    setForm({
      code: p.code || '', name: p.name || '', description: p.description || '',
      discount_type: p.discount_type || 'percentage',
      discount_value: p.discount_value?.toString() || '',
      min_order_value: p.min_order_value?.toString() || '',
      max_discount: p.max_discount?.toString() || '',
      max_uses: p.max_uses?.toString() || '',
      max_uses_per_user: p.max_uses_per_user?.toString() || '1',
      valid_from: p.valid_from || '', valid_until: p.valid_until || '',
      is_active: p.is_active !== false,
      applicable_to: p.applicable_to || 'all',
      restaurant_ids: p.restaurant_ids || []
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      discount_value: parseFloat(form.discount_value) || 0,
      min_order_value: parseFloat(form.min_order_value) || 0,
      max_discount: form.max_discount ? parseFloat(form.max_discount) : null,
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      max_uses_per_user: parseInt(form.max_uses_per_user) || 1,
    };

    try {
      const url = editData
        ? `${API_URL}/api/admin/promotions/${editData.promo_id}`
        : `${API_URL}/api/admin/promotions`;
      const res = await fetch(url, {
        method: editData ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success(language === 'pt' ? 'Promocao guardada!' : 'Promotion saved!');
        setDialogOpen(false);
        fetchPromotions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro');
      }
    } catch (err) { toast.error('Erro'); }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm(language === 'pt' ? 'Eliminar esta promocao?' : 'Delete this promotion?')) return;
    try {
      await fetch(`${API_URL}/api/admin/promotions/${promoId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(language === 'pt' ? 'Eliminado!' : 'Deleted!');
      fetchPromotions();
    } catch (err) { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-[#004E64] flex items-center gap-2">
          <Ticket className="text-[#FF6B35]" size={20} />
          {language === 'pt' ? 'Promocoes e Cupoes' : 'Promotions & Coupons'}
        </h2>
        <Button size="sm" className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full" onClick={openNew} data-testid="add-promotion-btn">
          <Plus size={14} className="mr-1" /> {language === 'pt' ? 'Nova Promocao' : 'New Promotion'}
        </Button>
      </div>

      {promotions.length === 0 ? (
        <Card className="rounded-2xl">
          <CardContent className="p-8 text-center">
            <Ticket size={40} className="mx-auto text-[#9FA2B4] mb-3" />
            <p className="text-[#9FA2B4]">{language === 'pt' ? 'Sem promocoes criadas' : 'No promotions created'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {promotions.map(p => (
            <Card key={p.promo_id} className={`rounded-2xl ${!p.is_active ? 'opacity-60' : ''}`} data-testid={`promo-card-${p.promo_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-[#FF6B35]/10 text-[#FF6B35] font-mono text-sm">{p.code}</Badge>
                      {p.is_active ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">{language === 'pt' ? 'Ativo' : 'Active'}</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500 text-xs">{language === 'pt' ? 'Inativo' : 'Inactive'}</Badge>
                      )}
                    </div>
                    <p className="font-semibold text-[#004E64] text-sm">{p.name || p.code}</p>
                    {p.description && <p className="text-xs text-[#9FA2B4]">{p.description}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)}>
                      <Edit size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(p.promo_id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#9FA2B4]">
                  <span className="flex items-center gap-1">
                    {p.discount_type === 'percentage' ? <Percent size={12} /> : <DollarSign size={12} />}
                    {p.discount_type === 'percentage' ? `${p.discount_value}%` : `${p.discount_value} EUR`}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users size={12} /> {p.uses_count || 0}{p.max_uses ? `/${p.max_uses}` : ''} {language === 'pt' ? 'usos' : 'uses'}
                  </span>
                  {p.min_order_value > 0 && <span>Min: {p.min_order_value} EUR</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editData ? (language === 'pt' ? 'Editar Promocao' : 'Edit Promotion') : (language === 'pt' ? 'Nova Promocao' : 'New Promotion')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Codigo' : 'Code'} *</Label>
                <Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} className="rounded-xl mt-1 font-mono" placeholder="VERAO2026" data-testid="promo-code-input" />
              </div>
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Nome' : 'Name'}</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" placeholder="Promo Verao" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{language === 'pt' ? 'Descricao' : 'Description'}</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-xl mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Tipo Desconto' : 'Discount Type'}</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{language === 'pt' ? 'Percentagem (%)' : 'Percentage (%)'}</SelectItem>
                    <SelectItem value="fixed">{language === 'pt' ? 'Valor Fixo (EUR)' : 'Fixed (EUR)'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Valor Desconto' : 'Discount Value'} *</Label>
                <Input type="number" value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} className="rounded-xl mt-1" placeholder={form.discount_type === 'percentage' ? '10' : '5.00'} data-testid="promo-discount-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Pedido Min (EUR)' : 'Min Order (EUR)'}</Label>
                <Input type="number" value={form.min_order_value} onChange={e => setForm({...form, min_order_value: e.target.value})} className="rounded-xl mt-1" placeholder="0" />
              </div>
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Desc. Maximo (EUR)' : 'Max Discount (EUR)'}</Label>
                <Input type="number" value={form.max_discount} onChange={e => setForm({...form, max_discount: e.target.value})} className="rounded-xl mt-1" placeholder="Sem limite" />
              </div>
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Usos Maximos' : 'Max Uses'}</Label>
                <Input type="number" value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})} className="rounded-xl mt-1" placeholder="Ilimitado" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Valido De' : 'Valid From'}</Label>
                <Input type="date" value={form.valid_from} onChange={e => setForm({...form, valid_from: e.target.value})} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Valido Ate' : 'Valid Until'}</Label>
                <Input type="date" value={form.valid_until} onChange={e => setForm({...form, valid_until: e.target.value})} className="rounded-xl mt-1" />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} data-testid="promo-active-switch" />
              <Label className="text-sm">{language === 'pt' ? 'Ativo' : 'Active'}</Label>
            </div>

            {/* Scope: where this coupon can be used */}
            <div className="bg-[#F4F1EA] rounded-2xl p-4 space-y-3">
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Aplicável a' : 'Applies to'}</Label>
                <Select value={form.applicable_to} onValueChange={v => setForm({ ...form, applicable_to: v, restaurant_ids: v === 'restaurants' ? form.restaurant_ids : [] })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="promo-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'pt' ? 'Plataforma inteira (todos os restaurantes)' : 'Whole platform (every restaurant)'}</SelectItem>
                    <SelectItem value="restaurants">{language === 'pt' ? 'Restaurantes específicos' : 'Specific restaurants'}</SelectItem>
                    <SelectItem value="services">{language === 'pt' ? 'Apenas Shisha / Gelo' : 'Shisha / Ice only'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.applicable_to === 'restaurants' && (
                <div>
                  <Label className="text-xs">{language === 'pt' ? 'Restaurantes (selecione um ou vários)' : 'Restaurants (pick one or more)'}</Label>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-44 overflow-y-auto p-2 bg-white rounded-xl border border-[#004E64]/10" data-testid="promo-restaurants-list">
                    {restaurants.map(r => {
                      const checked = form.restaurant_ids.includes(r.restaurant_id);
                      return (
                        <label
                          key={r.restaurant_id}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm ${checked ? 'bg-[#FF6B35]/10 border border-[#FF6B35]/30' : 'hover:bg-[#FAFAF7]'}`}
                          data-testid={`promo-rest-${r.restaurant_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setForm(f => ({
                              ...f,
                              restaurant_ids: checked ? f.restaurant_ids.filter(x => x !== r.restaurant_id) : [...f.restaurant_ids, r.restaurant_id]
                            }))}
                            className="accent-[#FF6B35]"
                          />
                          <span className="truncate">{r.name}</span>
                        </label>
                      );
                    })}
                    {restaurants.length === 0 && <p className="text-xs text-[#9FA2B4]">Sem restaurantes disponíveis.</p>}
                  </div>
                </div>
              )}
            </div>

            <Button onClick={handleSave} className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full mt-2" data-testid="save-promotion-btn">
              <Save size={16} className="mr-2" />
              {language === 'pt' ? 'Guardar' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromotionsManagement;
